import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Shield, Award, Users, Globe, Play } from 'lucide-react';
import { GlassCard } from './GlassCard';
import { cn } from '../utils/cn';
export function Hero() {
  return <section className="relative min-h-screen flex items-center pt-20 overflow-hidden" data-id="element-153">
      {/* Background Video with Overlay */}
      <div className="absolute inset-0 z-0" data-id="element-154">
        <video autoPlay muted loop playsInline className="w-full h-full object-cover opacity-30" data-id="element-155">
          <source src="https://www.cyberreadysecurity.info/wp-content/themes/cyberreadyip212/images/slider/slider.mp4" type="video/mp4" data-id="element-156" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-background via-background/80 to-background" data-id="element-157" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(24,99,220,0.15)_0%,transparent_70%)]" data-id="element-158" />
      </div>

      {/* Animated Cyber Grid/Mesh */}
      <div className="absolute inset-0 z-0 opacity-20 pointer-events-none" data-id="element-159">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#1e293b_1px,transparent_1px),linear-gradient(to_bottom,#1e293b_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" data-id="element-160" />
      </div>

      <div className="container mx-auto px-6 relative z-10" data-id="element-161">
        <div className="grid lg:grid-cols-2 gap-12 items-center" data-id="element-162">
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          duration: 0.8
        }} data-id="element-163">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary-blue/10 border border-primary-blue/20 text-primary-blue text-xs font-bold uppercase tracking-wider mb-6" data-id="element-164">
              <span className="relative flex h-2 w-2" data-id="element-165">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-blue opacity-75" data-id="element-166"></span>
                <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-blue" data-id="element-167"></span>
              </span>
              Elite Cybersecurity Academy
            </div>

            <h1 className="text-5xl md:text-7xl font-bold font-heading text-white leading-[1.1] mb-6" data-id="element-168">
              Accelerate Your <br data-id="element-169" />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-blue to-accent-cyan" data-id="element-170">
                Cybersecurity Career
              </span>
            </h1>

            <p className="text-lg md:text-xl text-white/60 max-w-xl mb-10 leading-relaxed" data-id="element-171">
              Master the skills that organizations demand in Governance, Risk,
              Compliance, Data Privacy, Executive Protection, and Cybersecurity
              Resilience.
            </p>

            <div className="flex flex-wrap gap-4" data-id="element-172">
              <button className="group relative px-8 py-4 bg-primary-blue text-white font-bold rounded-full overflow-hidden transition-all hover:scale-105 active:scale-95 shadow-xl shadow-primary-blue/20" data-id="element-173">
                <span className="relative z-10 flex items-center gap-2" data-id="element-174">
                  Explore Programs{' '}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" data-id="element-175" />
                </span>
                <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-accent-blue opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-176" />
              </button>
              <button className="px-8 py-4 bg-white/5 border border-white/10 text-white font-bold rounded-full hover:bg-white/10 transition-all" data-id="element-177">
                Student Portal
              </button>
            </div>

            <div className="mt-12 flex flex-wrap gap-6" data-id="element-178">
              {[{
              icon: Shield,
              text: 'Industry Expert Instructors'
            }, {
              icon: Globe,
              text: 'Global Alumni Network'
            }, {
              icon: Award,
              text: 'Certification Preparation'
            }].map((item, i) => <div key={i} className="flex items-center gap-2 text-white/50 text-sm" data-id="element-179">
                  <item.icon size={16} className="text-primary-blue" data-id="element-180" />
                  <span data-id="element-181">{item.text}</span>
                </div>)}
            </div>
          </motion.div>

          {/* Dashboard Panel */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.9,
          y: 30
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} transition={{
          duration: 1,
          delay: 0.2
        }} className="relative hidden lg:block" data-id="element-182">
            <div className="absolute -inset-4 bg-primary-blue/20 blur-3xl rounded-full opacity-50" data-id="element-183" />
            <GlassCard className="p-1 border-white/20 shadow-2xl" data-id="element-184">
              <div className="bg-background/40 rounded-xl overflow-hidden border border-white/5" data-id="element-185">
                <div className="flex items-center justify-between p-4 border-b border-white/10 bg-white/5" data-id="element-186">
                  <div className="flex gap-1.5" data-id="element-187">
                    <div className="w-3 h-3 rounded-full bg-red-500/50" data-id="element-188" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/50" data-id="element-189" />
                    <div className="w-3 h-3 rounded-full bg-green-500/50" data-id="element-190" />
                  </div>
                  <div className="text-[10px] text-white/30 uppercase tracking-widest font-bold" data-id="element-191">
                    CRS Command Center
                  </div>
                </div>

                <div className="p-6 space-y-6" data-id="element-192">
                  <div className="grid grid-cols-2 gap-4" data-id="element-193">
                    <div className="space-y-1" data-id="element-194">
                      <div className="text-[10px] text-white/40 uppercase font-bold" data-id="element-195">
                        Student Success Rate
                      </div>
                      <div className="text-2xl font-bold text-white" data-id="element-196">95.4%</div>
                      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-197">
                        <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: '95.4%'
                      }} transition={{
                        duration: 1.5,
                        delay: 1
                      }} className="h-full bg-primary-blue" data-id="element-198" />
                      </div>
                    </div>
                    <div className="space-y-1" data-id="element-199">
                      <div className="text-[10px] text-white/40 uppercase font-bold" data-id="element-200">
                        Global Presence
                      </div>
                      <div className="text-2xl font-bold text-white" data-id="element-201">
                        12+{' '}
                        <span className="text-sm font-normal text-white/40" data-id="element-202">
                          Countries
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-3" data-id="element-203">
                    <div className="text-[10px] text-white/40 uppercase font-bold" data-id="element-204">
                      Active Training Tracks
                    </div>
                    {[{
                    name: 'GRC Specialist',
                    progress: 75,
                    color: 'bg-primary-blue'
                  }, {
                    name: 'Cyber Resilience',
                    progress: 45,
                    color: 'bg-accent-cyan'
                  }, {
                    name: 'Data Privacy',
                    progress: 90,
                    color: 'bg-status-success'
                  }].map(track => <div key={track.name} className="space-y-1" data-id="element-205">
                        <div className="flex justify-between text-xs" data-id="element-206">
                          <span className="text-white/70" data-id="element-207">{track.name}</span>
                          <span className="text-white/40" data-id="element-208">
                            {track.progress}%
                          </span>
                        </div>
                        <div className="h-1.5 w-full bg-white/5 rounded-full overflow-hidden" data-id="element-209">
                          <motion.div initial={{
                        width: 0
                      }} animate={{
                        width: `${track.progress}%`
                      }} transition={{
                        duration: 1.5,
                        delay: 1.2
                      }} className={cn('h-full', track.color)} data-id="element-210" />
                        </div>
                      </div>)}
                  </div>

                  <div className="pt-4 border-t border-white/10 flex items-center justify-between" data-id="element-211">
                    <div className="flex -space-x-2" data-id="element-212">
                      {[1, 2, 3, 4].map(i => <div key={i} className="w-8 h-8 rounded-full border-2 border-background bg-white/10 flex items-center justify-center text-[10px] text-white overflow-hidden" data-id="element-213">
                          <img src={`https://i.pravatar.cc/100?img=${i + 10}`} alt="User" data-id="element-214" />
                        </div>)}
                      <div className="w-8 h-8 rounded-full border-2 border-background bg-primary-blue flex items-center justify-center text-[10px] text-white font-bold" data-id="element-215">
                        +1k
                      </div>
                    </div>
                    <div className="text-[10px] text-white/60" data-id="element-216">
                      Join 1,000+ Students
                    </div>
                  </div>
                </div>
              </div>
            </GlassCard>

            {/* Floating Elements */}
            <motion.div animate={{
            y: [0, -10, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            ease: 'easeInOut'
          }} className="absolute -top-6 -right-6" data-id="element-217">
              <GlassCard className="px-4 py-2 flex items-center gap-2 border-white/20" data-id="element-218">
                <div className="w-2 h-2 rounded-full bg-status-success animate-pulse" data-id="element-219" />
                <span className="text-xs font-bold text-white" data-id="element-220">
                  Live Class: GRC Masterclass
                </span>
              </GlassCard>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>;
}